/**
 * InlineSpanReplaceLayer.tsx — PLAT-10272 (T12, epic PLAT-10209)
 *
 * Inline "Fix Translation" span-replace for a shared translated transcript view
 * (the prose `TranscriptionTextTranslated`), reusing the PLAT-10214 workspace
 * core verbatim: the `useSpanReplace` hook + `SpanReplaceTrigger` +
 * `ReplaceSpanEditor`. Unlike the 3-pane workspace it has NO original-side lock
 * (there is no original column inline) and NO 3-pane chrome (epic D3) — just the
 * trigger + editor anchored over the selection.
 *
 * Mounted ONLY when a host opts in (epic D9 containment) and a foreign/translated
 * language is shown — `TranscriptionTextTranslated` only renders for a
 * translation, so this layer is foreign-gated by construction. Because the layer
 * is rendered conditionally, `useSpanReplace` (and its `useStore` /
 * `selectionchange` listener) never runs on surfaces that don't enable it.
 *
 * Confirm authors a SINGLE-fragment `origin: 'manual'` override (`fragmenter:
 * undefined`); fine-splitting stays the workspace's job (T9). The conflict /
 * cross-paragraph / re-edit guards ride along from the shared hook.
 *
 * PLAT-10257 (A3) — this layer also wires the DIRECTED re-translate mode, so the
 * editor's "Type Replacement | Re-translate" toggle appears here exactly as it
 * does in the workspace. The editor gates the toggle on the host passing
 * `onConfirmDirected`, so before this was wired the inline surfaces (the prose
 * translated transcript and the translated captions view) could only ever offer
 * the manual replacement — the toggle was absent by construction, not by rule.
 */

import { useTranslationStore } from '@src/stores-v2/StoreContext';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import React, { useRef } from 'react';

import {
  SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE,
  spanBlockMessage,
  useSpanReplace,
} from './hooks/useSpanReplace';
import { ReplaceSpanEditor } from './ReplaceSpanEditor';

import type { ReDubSelectionRequest } from './hooks/useSpanReplace';
import type { PendingSpan } from './hooks/useSpanReplace';
import { SpanReplaceTrigger } from './SpanReplaceTrigger';

import type { RefObject } from 'react';

interface InlineSpanReplaceLayerProps {
  /**
   * The local container holding this view's `data-initial-index` word spans —
   * the selection is read and the affordance is scoped to it (CLAUDE.md #12:
   * three transcript instances can coexist; never use `document`).
   */
  containerRef: RefObject<HTMLElement>;
  /** The translated (foreign) language under edit. */
  language: string;
  /**
   * PLAT-10646 — hide the floating trigger pill. The extended translated
   * context menu hosts the "Fix Translation" entry instead (which opens the
   * same span editor via `openEditorRef`), so the pill would be a duplicate.
   */
  hideTrigger?: boolean;
  /**
   * PLAT-10646 — imperative opener for the pending span's editor, for the
   * context-menu entry. Returns false when no span is pending (the caller
   * falls back to the single-phrase editor).
   */
  openEditorRef?: React.MutableRefObject<(() => boolean) | null>;
  /**
   * PLAT-10833 — the span layer's imperative re-dub, for the extended context
   * menu's Re-dub AI Voice row (which hides this layer's own trigger).
   * `available` answers whether the pending selection covers dub lines;
   * `run` hands them to the host. Reassigned each render.
   */
  reDubRef?: React.MutableRefObject<{
    available: () => boolean;
    run: () => boolean;
    /** PLAT-12056 — the selection's span, for the Fix Translation hand-off. */
    selection: () => PendingSpan | null;
  } | null>;
  /**
   * PLAT-10616 — re-dub the selected span. When provided, the selection menu
   * offers "Re-dub AI Voice" alongside Fix Translation; it receives the
   * selection (its dub line ids, speakers and anchor) and the host opens the
   * selection re-dub popover (PLAT-12022). Omitted ⇒ only Fix Translation
   * shows.
   */
  onRequestReDub?: (request: ReDubSelectionRequest) => void;
}

export const InlineSpanReplaceLayer: React.FC<InlineSpanReplaceLayerProps> = ({
  containerRef,
  language,
  hideTrigger = false,
  openEditorRef,
  reDubRef,
  onRequestReDub,
}) => {
  // No original-side lock inline (there is no original column). A null ref makes
  // the hook's lock effect a no-op (`if (!original) return`).
  const noOriginalColumnRef = useRef<HTMLElement>(null);

  // Source-language display name for the editor's collapsed "Original ·
  // <language>" disclosure (PLAT-10644).
  const sourceLanguage =
    useTranslationStore().getTranslation(language)?.sourceLanguage;

  const spanReplace = useSpanReplace({
    enabled: true,
    language,
    // Prose view = one word per phrase (`data-initial-index` per phrase), never
    // the aligned Segment view — so no segment expansion.
    viewMode: 'paragraph',
    translatedColumnRef: containerRef,
    originalColumnRef: noOriginalColumnRef,
    // Single deferred fragment (T12); the workspace handles fine-splitting (T9).
    fragmenter: undefined,
    onRequestReDub,
  });

  // Keep the imperative opener pointing at the latest hook state (no dep
  // array — reassigned each render, cleared on unmount).
  React.useEffect(() => {
    if (!openEditorRef) return;
    openEditorRef.current = () => {
      if (!spanReplace.pendingSpan) return false;
      spanReplace.openEditor();
      return true;
    };
    return () => {
      openEditorRef.current = null;
    };
  });
  React.useEffect(() => {
    if (!reDubRef) return;
    reDubRef.current = {
      available: spanReplace.canReDubSelection,
      run: spanReplace.reDubSelection,
      selection: spanReplace.peekSelectionSpan,
    };
    return () => {
      reDubRef.current = null;
    };
  });

  return (
    <>
      {!hideTrigger && spanReplace.pendingSpan && !spanReplace.editor && (
        <SpanReplaceTrigger
          anchorElement={spanReplace.pendingSpan.anchorElement}
          onFixTranslation={spanReplace.openEditor}
          onReDub={spanReplace.canReDub ? spanReplace.reDub : undefined}
        />
      )}
      {spanReplace.editor && (
        /* Remount when the span's stored text changes under the open editor.
           The input pre-fills from `currentValue` ONCE, so if the span is
           reopened over changed store data (a revert, a reopen from the
           coverage popover) the previous text would still be sitting in the
           box, one Save away from being written straight back.
           `editableValue` is a snapshot taken when the span is (re)opened, so
           this changes only on a genuine reopen — never while the reviewer is
           typing. */
        <ReplaceSpanEditor
          key={spanReplace.editor.editableValue}
          anchorElement={spanReplace.editor.anchorElement}
          sourceText={spanReplace.editor.model.originalSnapshot}
          sourceLanguageLabel={
            sourceLanguage ? formatLanguageCaption(sourceLanguage) : undefined
          }
          currentValue={spanReplace.editor.editableValue}
          originalValue={spanReplace.editor.originalTranslatedValue}
          wordCount={spanReplace.editor.model.coveredIndices.length}
          replacingWordCount={spanReplace.editor.replacingWordCount}
          onConfirm={spanReplace.confirm}
          onCancel={spanReplace.cancel}
          onRevert={spanReplace.revertOverride}
          revertDisabled={spanReplace.revertDisabled}
          blockedMessage={
            spanReplace.editor.blockedReason
              ? spanBlockMessage(spanReplace.editor.blockedReason)
              : undefined
          }
          // PLAT-10257 (A3) — directed re-translate, same as the workspace: the
          // second mode behind the same entry, no new trigger item. The editor
          // only renders the mode toggle when a host wires this, so omitting it
          // is what left this surface manual-only. `fragmenter` stays undefined
          // here, so a directed override lands as ONE coarse piece — the same
          // shape `confirm` authors inline (fine-splitting is the workspace's).
          onConfirmDirected={spanReplace.confirmDirected}
          // The RE-EDIT (within-fix) case AND the absorb case (PLAT-11655):
          // manual editing stays, instruction mode is unavailable (the directed
          // path refuses any span touching a correction), and switching to it
          // shows the overlap notice.
          instructionModeBlockedMessage={
            spanReplace.editor.model.existingOverrideSegmentId ||
            spanReplace.editor.overlappingOverrideSegmentIds?.length
              ? SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE
              : undefined
          }
        />
      )}
    </>
  );
};
